import React, { useEffect, useState, useCallback, useLayoutEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

/**
 * Lightweight custom guided-tour for the provider portal.
 *
 * Why custom (not react-joyride):
 *   react-joyride v3 + React 19 has known rendering issues where the overlay
 *   paints but the spotlight + tooltip don't. This is ~150 lines and removes
 *   the dependency entirely.
 *
 * Behavior:
 * - Auto-starts on the user's first visit to the dashboard (gated by
 *   localStorage['sendero_tour_completed']).
 * - Re-triggered by setting localStorage['sendero_tour_start']='1' and
 *   navigating to '/' (the ChatAssistant "Take a quick tour" chip does this).
 *
 * Step targets reference DOM elements via [data-tour=...] attributes added on
 * the dashboard and main layout, so the tour is decoupled from component
 * internals.
 */

const TOUR_DONE_KEY = 'sendero_tour_completed';
const TOUR_START_KEY = 'sendero_tour_start';
const SPOTLIGHT_PAD = 8;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_MARGIN = 16;
const OVERLAY_COLOR = 'rgba(15, 23, 42, 0.55)';

const steps = [
  {
    target: null, // welcome - centered modal
    title: 'Welcome to your Provider Portal',
    content: "A quick 60-second tour of the Home dashboard. For step-by-step help on any other page, ask the assistant in the bottom-right — try “Get help with a task” → “Guide me through a page.”",
  },
  {
    target: '[data-tour="sidebar-nav"]',
    placement: 'right',
    title: 'Main navigation',
    content: 'Use the left sidebar to jump between Home, Eligibility & Benefits, Claims & Payments, EOP Search, Forms, and admin tools.',
  },
  {
    target: '[data-tour="stat-cards"]',
    placement: 'bottom',
    title: 'Your portal at a glance',
    content: 'Active members, pending claims, and outstanding claims are summarized right at the top of the dashboard.',
  },
  {
    target: '[data-tour="quick-actions"]',
    placement: 'top',
    title: 'Quick actions',
    content: 'Jump straight to checking member eligibility or viewing claims.',
  },
  {
    target: '[data-tour="global-search"]',
    placement: 'bottom',
    title: 'Search anything',
    content: 'Find claims, authorizations, members, EOPs, and pages fast from the top-bar search.',
  },
  {
    target: '[data-tour="help-icon"]',
    placement: 'bottom',
    title: 'Help anytime',
    content: 'Click the ? icon to open Help & Support with FAQs and contact info.',
  },
  {
    target: '[data-tour="assistant-fab"]',
    placement: 'left',
    title: 'Ask the assistant',
    content: 'The Central Health Assistant looks up claims, eligibility, and payments — and walks you through every page and task. Tap “Get help with a task” → “Guide me through a page.”',
  },
];

const AppTour = () => {
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  });
  const tooltipRef = useRef(null);

  // Decide whether to run the tour. Auto-start on the dashboard if the user
  // hasn't completed it OR they explicitly requested a restart.
  useEffect(() => {
    if (location.pathname !== '/') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRun(false);
      return;
    }
    const requestedRestart = localStorage.getItem(TOUR_START_KEY) === '1';
    const alreadyDone = localStorage.getItem(TOUR_DONE_KEY) === '1';
    if (requestedRestart || !alreadyDone) {
      const timer = setTimeout(() => {
        setStepIndex(0);
        setRun(true);
        if (requestedRestart) localStorage.removeItem(TOUR_START_KEY);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  // Track viewport dimensions for clamping.
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Explicit "start the tour now" trigger (chat chip, Help page). Works even when
  // the user is already on the dashboard (where navigate('/') is a no-op and the
  // route effect wouldn't re-fire). If on another route, the caller navigates to
  // '/' and the route effect above starts it.
  useEffect(() => {
    const onStart = () => {
      if (window.location.pathname === '/') {
        setStepIndex(0);
        setRun(true);
        try { localStorage.removeItem(TOUR_START_KEY); } catch { /* ignore */ }
      }
    };
    window.addEventListener('sendero:start-tour', onStart);
    return () => window.removeEventListener('sendero:start-tour', onStart);
  }, []);

  // While running: lock body scroll, kill scroll-anchoring + smooth-scroll so
  // measurements settle. Restore on cleanup.
  useEffect(() => {
    if (!run) return;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      bodyAnchor: document.body.style.overflowAnchor,
      htmlScroll: document.documentElement.style.scrollBehavior,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.overflowAnchor = 'none';
    document.documentElement.style.scrollBehavior = 'auto';
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.overflowAnchor = prev.bodyAnchor;
      document.documentElement.style.scrollBehavior = prev.htmlScroll;
    };
  }, [run]);

  // Compute target rect when step changes — re-measures after a small delay to
  // allow scroll-into-view to complete.
  useLayoutEffect(() => {
    if (!run) return;
    const step = steps[stepIndex];
    if (!step) return;

    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!step.target) { setRect(null); return; }

    let cancelled = false;
    const rafIds = new Set();
    const timerIds = new Set();

    const isOffscreen = (r) =>
      r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth;

    const scrollTargetIntoView = (el, placement) => {
      const r = el.getBoundingClientRect();
      if (!isOffscreen(r)) return;
      const absoluteTop = r.top + window.scrollY;
      let topOffset;
      if (placement === 'top') topOffset = Math.min(window.innerHeight * 0.6, window.innerHeight - 100);
      else if (placement === 'left' || placement === 'right') topOffset = window.innerHeight * 0.3;
      else topOffset = window.innerHeight * 0.15;
      window.scrollTo({ top: Math.max(0, absoluteTop - topOffset), behavior: 'auto' });
    };

    const attempt = (n) => {
      if (cancelled) return;
      const el = document.querySelector(step.target);
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      if (n < 4 && isOffscreen(r)) {
        scrollTargetIntoView(el, step.placement);
        const id1 = requestAnimationFrame(() => {
          rafIds.delete(id1);
          const id2 = requestAnimationFrame(() => { rafIds.delete(id2); attempt(n + 1); });
          rafIds.add(id2);
        });
        rafIds.add(id1);
        return;
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    const el = document.querySelector(step.target);
    if (el) scrollTargetIntoView(el, step.placement);
    const id = requestAnimationFrame(() => { rafIds.delete(id); attempt(0); });
    rafIds.add(id);

    const t1 = setTimeout(() => attempt(0), 200);
    const t2 = setTimeout(() => attempt(0), 450);
    timerIds.add(t1);
    timerIds.add(t2);

    const onResize = () => {
      const el2 = document.querySelector(step.target);
      if (!el2) return;
      const r = el2.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelled = true;
      rafIds.forEach((id) => cancelAnimationFrame(id));
      timerIds.forEach((id) => clearTimeout(id));
      window.removeEventListener('resize', onResize);
    };
  }, [run, stepIndex, viewport.w, viewport.h]);

  const finish = useCallback(() => {
    setRun(false);
    localStorage.setItem(TOUR_DONE_KEY, '1');
  }, []);

  const handleNext = useCallback(() => {
    if (stepIndex >= steps.length - 1) finish();
    else setStepIndex((i) => i + 1);
  }, [stepIndex, finish]);

  const handleBack = useCallback(() => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  }, [stepIndex]);

  // Keyboard nav: Esc to skip, arrows/Enter to navigate.
  useEffect(() => {
    if (!run) return;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      // preventDefault on Enter so a focused Next/Close button doesn't ALSO
      // activate from the same keypress (double-advance).
      if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); handleNext(); }
      if (e.key === 'ArrowLeft') handleBack();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [run, finish, handleNext, handleBack]);

  if (!run || location.pathname !== '/') return null;

  const step = steps[stepIndex];
  if (!step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;
  const isWelcome = !step.target;
  const hasRect = rect && rect.width > 0 && rect.height > 0;

  // Compute tooltip position with viewport clamping.
  let tooltipStyle;
  if (isWelcome || !hasRect) {
    tooltipStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  } else {
    const placement = step.placement || 'bottom';
    let top, left, transform;
    switch (placement) {
      case 'top':
        top = rect.top - TOOLTIP_MARGIN;
        left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        transform = 'translateY(-100%)';
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - TOOLTIP_MARGIN - TOOLTIP_WIDTH;
        transform = 'translateY(-50%)';
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.left + rect.width + TOOLTIP_MARGIN;
        transform = 'translateY(-50%)';
        break;
      case 'bottom':
      default:
        top = rect.top + rect.height + TOOLTIP_MARGIN;
        left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
        transform = 'none';
    }
    const minLeft = 12;
    const maxLeft = viewport.w - TOOLTIP_WIDTH - 12;
    left = Math.max(minLeft, Math.min(maxLeft, left));
    const TOOLTIP_H = 260;
    if (placement === 'left' || placement === 'right') {
      const minCenter = 12 + TOOLTIP_H / 2;
      const maxCenter = viewport.h - 12 - TOOLTIP_H / 2;
      top = Math.max(minCenter, Math.min(maxCenter, top));
    }
    if (placement === 'top' && rect.top - TOOLTIP_H - TOOLTIP_MARGIN < 12) {
      if (rect.top + rect.height + TOOLTIP_MARGIN + TOOLTIP_H < viewport.h - 12) {
        top = rect.top + rect.height + TOOLTIP_MARGIN; transform = 'none';
      } else { top = 16; transform = 'none'; }
    } else if (placement === 'bottom' && top + TOOLTIP_H > viewport.h - 12) {
      if (rect.top - TOOLTIP_MARGIN - TOOLTIP_H >= 12) {
        top = rect.top - TOOLTIP_MARGIN; transform = 'translateY(-100%)';
      } else { top = 16; transform = 'none'; }
    }
    tooltipStyle = { top, left, transform };
  }

  const spot = hasRect
    ? {
        top: Math.max(0, rect.top - SPOTLIGHT_PAD),
        left: Math.max(0, rect.left - SPOTLIGHT_PAD),
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
      }
    : null;

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={step.title}
      style={{ position: 'fixed', inset: 0, zIndex: 10000, pointerEvents: 'none' }}
    >
      {spot ? (
        <>
          <div onClick={finish} style={{ position: 'fixed', top: 0, left: 0, right: 0, height: spot.top, background: OVERLAY_COLOR, pointerEvents: 'auto', transition: 'all 0.25s ease' }} />
          <div onClick={finish} style={{ position: 'fixed', top: spot.top + spot.height, left: 0, right: 0, bottom: 0, background: OVERLAY_COLOR, pointerEvents: 'auto', transition: 'all 0.25s ease' }} />
          <div onClick={finish} style={{ position: 'fixed', top: spot.top, left: 0, width: spot.left, height: spot.height, background: OVERLAY_COLOR, pointerEvents: 'auto', transition: 'all 0.25s ease' }} />
          <div onClick={finish} style={{ position: 'fixed', top: spot.top, left: spot.left + spot.width, right: 0, height: spot.height, background: OVERLAY_COLOR, pointerEvents: 'auto', transition: 'all 0.25s ease' }} />
          <div style={{ position: 'fixed', top: spot.top, left: spot.left, width: spot.width, height: spot.height, borderRadius: 10, boxShadow: '0 0 0 2px #0D9488, 0 0 0 6px rgba(13,148,136,0.25)', pointerEvents: 'none', transition: 'all 0.25s ease' }} />
        </>
      ) : (
        <div onClick={finish} style={{ position: 'fixed', inset: 0, background: OVERLAY_COLOR, pointerEvents: 'auto' }} />
      )}

      {/* Tooltip */}
      <div ref={tooltipRef} style={{ position: 'fixed', width: TOOLTIP_WIDTH, maxWidth: 'calc(100vw - 24px)', background: '#fff', borderRadius: 12, padding: '20px 22px', boxShadow: '0 14px 44px rgba(0,0,0,0.22)', pointerEvents: 'auto', ...tooltipStyle }}>
        <button onClick={finish} aria-label="Close" style={{ position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: 8, border: 'none', background: 'transparent', color: '#94A3B8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <X size={16} />
        </button>
        <div style={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, marginBottom: 6 }}>{stepIndex + 1} / {steps.length}</div>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0F172A', marginBottom: 8, paddingRight: 24 }}>{step.title}</h3>
        <p style={{ margin: 0, fontSize: 14, color: '#475569', lineHeight: 1.55, marginBottom: 18 }}>{step.content}</p>
        <div style={{ height: 4, background: '#E2E8F0', borderRadius: 2, overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ height: '100%', width: `${((stepIndex + 1) / steps.length) * 100}%`, background: '#0D9488', borderRadius: 2, transition: 'width 0.3s ease' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <button onClick={finish} style={{ background: 'none', border: 'none', color: '#94A3B8', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 4px' }}>Skip tour</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isFirst && (
              <button onClick={handleBack} style={{ background: '#F1F5F9', border: 'none', color: '#475569', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '8px 16px', borderRadius: 8 }}>Back</button>
            )}
            <button onClick={handleNext} style={{ background: '#0D9488', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', padding: '8px 18px', borderRadius: 8 }}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AppTour;
